import React, { useEffect, useState, ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  CardFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { useAlertPlus, useConfirm } from 'hooks';
import Attachments from 'components/Requests/Attachments';
import Required from 'components/Required';
import ConfirmModal from 'components/ConfirmModal';
import AlertModal from 'components/AlertModal';
import ObservationsAlert from 'components/Requests/ObservationAlert';
import { useUser } from 'context/UserContext';
import { useHistory } from 'react-router-dom';
import InputError from 'components/InputError';
import { Request, RequestFiles, AttachmentType } from 'types/Request';
import DatePicker, { DatePickerChangeEvent } from 'components/DatePicker';
import { getRequest, updateRequest, createObservation, checkSession } from 'features/requests/requestsAPI';
import ObservationList from 'components/Requests/ObservationList';
import moment from 'moment';
import { getVencimiento } from 'helpers/vencimientos';
import FakeInput from 'components/FakeInput';

export default function Edit() {
  const { id } = useParams<{ id : string }>();

  const history = useHistory();

  const { getMatricula } = useUser();
  const matriculateId = getMatricula();
  if (!matriculateId) {
    return null;
  }

  const [disableSend, setDisableSend] = useState(false);

  const [newSessionDisabled, setNewSessionDisabled] = useState(true);
  const [newSessionDate, setNewSessionDate] = useState<Date | null>();
  const [newSessionDateMin, setNewSessionDateMin] = useState<Date>();
  const [newSessionDateMax, setNewSessionDateMax] = useState<Date>();
  const [showSessionsModal, setShowSessionsModal] = useState(false);

  const [infoVencimiento, setInfoVencimiento] = useState('');

  const [showAlert, message, alertCallback, alert] = useAlertPlus('');
  const [isLoading, setLoading] = useState(true);
  const [confirm, confirmTitle, confirmAction, setConfirm] = useConfirm(false);

  const [request, setRequest] = useState<Request>();

  const [files, setFiles] = useState<RequestFiles>({});

  const [formValidation, setFormValidation] = useState<Partial<Record<keyof Request, boolean>>>({});

  useEffect(() => {
    setLoading(true);

    getRequest(parseInt(id))
      .then(response => {
        if (!response) {
          return;
        }

        const { request, files: newFiles } = response;

        if (!request.id || !request.insurer_id) {
          alert('Se produjo un error al intentar recuperar la información de la presentación. ' +
          'Por favor, intente nuevamente más tarde',
          () => {
            window.location.href = '/solicitudes';
          });
          console.error('Edit-Swiss-D', `No se pudo recuperar información sobre la solicitud #${ id }` );
          console.info(request);
          return;
        }

        setFiles(newFiles);
        setRequest(request);
      })
      .catch(err => {
        setDisableSend(true);
        alert('Se produjo un error al intentar recuperar la información de la presentación. ' +
            'Por favor, intente nuevamente más tarde');
        console.error('Edit-Swiss-D', 'Error inesperador al intentar recuperar la presentación');
        console.info(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  // Control del estado del botón de agregar sesiones
  useEffect(() => {
    if (!request) {
      return;
    }

    // Si la autorización es previa y no hay fecha de prescripción no se puede agregar sesiones
    if (!request.prescription_date || !request.authorization_date) {
      setNewSessionDisabled(true);
      return;
    }

    setNewSessionDisabled(false);
  }, [
    request?.authorization_date,
    request?.prescription_date,
  ]);

  // Cálculo de días para el vencimiento
  useEffect(() => {
    if (!request?.sessions || ![1, 4].includes(request.status_id)) {
      setInfoVencimiento('');
      return;
    }

    const diffDays = getVencimiento(request.sessions);
    if (!diffDays) {
      setInfoVencimiento('');
      return;
    }

    if (diffDays > 60) {
      setInfoVencimiento('Solicitud vencida');
    } else {
      setInfoVencimiento(`Restan ${ 60 - diffDays } días para que se venza la solicitud`);
    }
  }, [
    request?.sessions.length,
    request?.status_id,
  ]);

  if (!request) {
    return <>Cargando...</>;
  }

  const handleBillingDraft = async () => {
    setFormValidation({});

    setLoading(true);

    updateRequest(
      {
        ...request,
        status_id: 1,
      },
      files,
    )
      .then(res => {
        if (!res || !res.request.status_id || res.request.status_id !== 1) {
          console.error('EditSwiss', 'Error al guardar la solicitud');
          console.info(res);
          alert(
            'Ha ocurrido un error al guardar la solicitud. Por favor intente nuevamente.',
          );
          setLoading(false);
          return;
        }

        alert('La solicitud se guardó correctamente', () => {
          history.push('/solicitudes');
        });
      })
      .catch(err => {
        console.error(
          'EditSwiss',
          'Error inesperado al intentar guardar la solicitud',
        );
        console.info(err);
        alert(
          'Ha ocurrido un error al guardar la solicitud. Por favor intente nuevamente.',
        );
        setLoading(false);
      });
  };

  const handleFormValidation = () => {
    if (!request) {
      return;
    }

    let newFormValidation = {};

    let toValidate = [
      ...Array.from(document.getElementsByClassName('form-control')),
    ] as HTMLInputElement[];
    toValidate = toValidate.filter(e => !e.disabled && e.required && e.type !== 'file');

    let missing = false;
    // Validar campos required
    toValidate.forEach(field => {
      if (field.required && !field.disabled && !field.value?.trim()) {
        field.classList.add('is-invalid');

        missing = true;
      } else if (field.inputMode === 'numeric') {
        if (!/^\d+$/.test(field.value)) {
          newFormValidation = {
            ...newFormValidation,
            [field.name]: false,
          };
        }
      } else {
        field.classList.remove('is-invalid');
      }
    });

    // Validar file inputs required
    toValidate = [
      ...Array.from(document.getElementsByClassName('custom-file-input')),
    ] as HTMLInputElement[];
    toValidate.forEach(field => {
      if (
        field.required &&
        !field.disabled &&
        !files[field.dataset.key as AttachmentType]
      ) {
        field.classList.add('is-invalid');

        missing = true;
      }
    });

    // Validar cantidad de sesiones
    if (request.sessions.length === 0) {
      newFormValidation = {
        ...newFormValidation,
        sessions: false,
      };

      missing = true;
    }

    if (missing) {
      setFormValidation(newFormValidation);
      alert(
        'Todos los campos marcados con * son obligatorios. ' +
          'Por favor complete los faltantes antes de enviar la solicitud.',
      );
      return false;
    }

    return true;
  };

  // Confirmación de envío de Solicitud
  const handleBillingConfirmation = () => {
    if (!handleFormValidation()) {
      return;
    }

    setConfirm(true, '¿Estás seguro que deseas enviar esta solicitud?', 'billing');
  };

  // Gestor de respuestas de confirmaciones
  const handleConfirmation = (action: string) => {
    if (action === 'billing') {
      handleBillingRequest();
    }

    setConfirm(false);
  };

  // Envío de solicitudes
  const handleBillingRequest = () => {
    setFormValidation({});

    setLoading(true);

    updateRequest(
      {
        ...request,
        status_id: 2,
      },
      files,
    )
      .then(res => {
        if (!res || !res.request.status_id) {
          // Error interno
          console.error('Edit-Swiss-D', 'Error al enviar la solicitud');
          console.info(res);
          alert('Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.');
          return;
        } else if (res.request.status_id === 10) {
          // Fuera de término
          alert(
            `La solicitud ${request.id} se encuentra fuera de término`,
            () => {
              history.push('/solicitudes');
            },
          );
          return;
        } else if (res.request.status_id !== 2 && res.request.status_id !== 3) {
          // Otros errores
          console.error('Edit-Swiss-D', 'Error al enviar la solicitud');
          console.info(res);
          alert(
            'Ha ocurrido un error al procesar la solicitud enviada. Por favor intente nuevamente.',
          );
          return;
        }

        alert('La solicitud se envió correctamente', () => {
          history.push('/solicitudes');
        });
      })
      .catch(err => {
        console.error(
          'Edit-Swiss-D',
          'Error inesperado al intentar enviar la solicitud',
        );
        console.info(err);
        alert(
          'Ha ocurrido un error inesperado al enviar la solicitud. Por favor intente nuevamente.',
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement> | DatePickerChangeEvent) => {
    const { target } = event;

    const name = target.name;

    let value: string | boolean | number | Date | null;
    value = target.type === 'checkbox' ? target.checked : target.value;
    if (typeof value === 'string' && ['insurer_id', 'practice_id', 'additional_id'].includes(name)) {
      value = parseInt(value);
    }

    if (name === 'recipient_age' && typeof value === 'string') {
      value = value.replace(/\D/g, '');
    }

    const extra: Partial<Request> = {};

    // Si cambió el número de afiliado, borramos el nombre
    if (name === 'recipient_id') {
      extra['recipient_name'] = undefined;
    }

    // Guardamos el nuevo valor
    setRequest({
      ...request,
      ...extra,
      [name]: value,
    } as Request);
  };

  const handleNewSession = async () => {
    if (!newSessionDate) {
      return;
    }

    const checkSessionResult = await checkSession(request, moment(newSessionDate).format('YYYY-MM-DD'));

    if (checkSessionResult !== true) {
      alert(checkSessionResult);
      return;
    }

    const newSessions = [
      ...request.sessions,
      {
        date: newSessionDate,
        date_parsed: newSessionDate.toISOString().split('T')[0],
        authorization_id: '',
        coinsurance: '0.0',
        online: !request.previously_authorized,
      },
    ].sort((a, b) => (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0));

    // Agregamos al arreglo de fechas de sesiones la nueva fecha
    setRequest({
      ...request,
      sessions: newSessions,
    });

    // Ocultamos el modal
    setShowSessionsModal(false);
  };

  const handleRemoveSession = (target: number) => {
    // Filtramos del arreglo de sesiones el índice correspondiente
    setRequest({
      ...request,
      sessions: request.sessions.filter((value, idx) => idx !== target),
    } as Request);
  };

  const showNewSessionModal = async () => {
    // Variables de fecha auxiliares
    const today = new Date();

    // Límite inferior para el rango de selección de fechas
    let sessionDateMin = moment().subtract(364, 'days');
    // Límite superior para el rango de selección de fechas
    let sessionDateMax = moment();
    // Fecha sugerida
    let sessionDate: Date | null = moment().startOf('year').toDate();

    // Si la fecha de prescripción es mayor a la fecha mínima, ajustamos la fecha mínima
    if (request?.prescription_date && sessionDateMin.isBefore(request.prescription_date, 'day')) {
      sessionDateMin = moment(request.prescription_date);
    }

    // Las sesiones de una prestación por discapacidad de Swiss tienen que estar dentro del mismo año calendario
    if (request.sessions.length > 0) {
      const firstSessionDate = request.sessions
        .map(({ date }) => date)
        .sort((a, b) => a.valueOf() - b.valueOf())[0];

      // Si el nuevo mínimo se va del año de la primera sesión lo ajustamos
      if (sessionDateMin.isBefore(firstSessionDate, 'year')) {
        sessionDateMin = moment(firstSessionDate).startOf('year');
      }

      // Si el nuevo máximo se va del año de la primera sesión lo ajustamos
      if (sessionDateMax.isAfter(firstSessionDate, 'year')) {
        sessionDateMax = moment(firstSessionDate).endOf('year');
      }
    }

    // Si no hay fecha de sesión definida (no se cargó ninguna), tomamos la fecha
    // mínima, si no tomamos el día siguiente a la última fecha
    if (request.sessions.length) {
      const lastSessionDate = request.sessions
        .map(({ date }) => date)
        .sort((a, b) => b.getTime() - a.getTime())[0];
      let tentativeSessionDate = moment(lastSessionDate.valueOf() + 1);

      // Controlamos que la fecha tentativa sea posterior al mínimo
      if (tentativeSessionDate.valueOf() < sessionDateMin.valueOf()) {
        tentativeSessionDate = sessionDateMin;
      }

      // Sólo tomamos la fecha tentativa si en menor a hoy
      if (tentativeSessionDate.valueOf() < today.valueOf()) {
        sessionDate = tentativeSessionDate.toDate();
      }
    }

    const invalidMin = moment(sessionDate).isBefore(sessionDateMin, 'day');
    const invalidMax = moment(sessionDate).isAfter(sessionDateMax, 'day');

    if (invalidMin) {
      sessionDate = sessionDateMin.toDate();
    } else if (invalidMax) {
      sessionDate = sessionDateMax.toDate();
    } else if (invalidMin && invalidMax) {
      sessionDate = null;
    }

    if (moment(sessionDateMin).isSameOrAfter(sessionDateMax, 'day')) {
      sessionDate = null;
    }

    // Seteamos todos los parámetros
    setNewSessionDateMin(sessionDateMin.toDate());
    setNewSessionDateMax(sessionDateMax.toDate());
    setNewSessionDate(sessionDate);

    // Mostramos el modal
    setShowSessionsModal(true);
  };

  const handleNewObservation = async (obserbation: string) => {
    setLoading(true);

    const result = await createObservation(request, obserbation);

    if (result.status === 'ok') {
      setRequest({
        ...request,
        observations: result.request.observations,
      });
    }

    setLoading(false);

    return result.status === 'ok';
  };

  return (
    <div className="content">
      <Row>
        <Col sm={12}>
          <Card>
            <CardHeader>
              <Row style={{ alignItems: 'center' }}>
                <Col sm={8}>
                  <CardTitle tag="h5">
                    Presentación #{id}
                    {
                      request?.insurer_shortname
                        ? ' - ' + request.insurer_shortname
                        : ' '
                    }
                    {
                      request?.impairment
                        ? ' (Discapacidad)'
                        : ' '
                    }
                    <br />
                    <span style={{ fontSize: '1rem' }}>{ infoVencimiento }</span>
                  </CardTitle>
                </Col>
                <Col sm={4} className="text-right">
                  <Button
                    className="btn-round"
                    color="primary"
                    onClick={() => history.push('/solicitudes')}
                  >
                    Volver
                  </Button>
                </Col>
              </Row>
              <ObservationsAlert observations={request.observations} />
            </CardHeader>
            <CardBody>
              <Form>
                <FormGroup>
                  <Row>
                    <Col sm={6}>
                      <Label for="authorization_id">
                        Número de Aprobación <Required />
                      </Label>
                      <Input
                        id="authorization_id"
                        name="authorization_id"
                        value={request.authorization_id ?? ''}
                        invalid={formValidation.authorization_id === false}
                        onChange={handleInputChange}
                        disabled={ isLoading || !request.previously_authorized }
                        required
                      />
                    </Col>
                    <Col sm={6}>
                      <Label for="authorization_date">
                        Fecha de Expedición
                        <Required />
                      </Label>
                      <DatePicker
                        id="authorization_date"
                        name="authorization_date"
                        selected={request.authorization_date ?? ''}
                        dateFormat="dd/MM/yyyy"
                        locale="es"
                        placeholderText="Fecha de Aprobación"
                        className="date-picker form-control"
                        minDate={ moment().subtract(365, 'days').toDate() }
                        maxDate={ new Date() }
                        disabled={ isLoading }
                        onChange={date => {
                          handleInputChange({
                            target: {
                              name: 'authorization_date',
                              value: date,
                              type: 'datepicker',
                            },
                          });
                        }}
                        autoComplete="off"
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col sm={6}>
                      <Label for="recipient_id">Número de Afiliado</Label>
                      <FakeInput value={ request.recipient_id ?? '' } />
                    </Col>
                    <Col sm={6}>
                      <Label for="recipient_name">Nombre del Afiliado</Label>
                      <FakeInput value={request.recipient_name ?? ''} />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col sm={6}>
                      <Label for="matricula_profesional_solicitante">
                        Matrícula Médico Prescriptor <Required />
                      </Label>
                      <Input
                        id="matricula_profesional_solicitante"
                        name="matricula_profesional_solicitante"
                        type="text"
                        invalid={formValidation.matricula_profesional_solicitante === false}
                        value={request.matricula_profesional_solicitante ?? ''}
                        placeholdertext="Médico Prescriptor"
                        className="form-control"
                        disabled={ isLoading }
                        onChange={handleInputChange}
                        required
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col sm={6}>
                      <Label for="prescription_date">
                        Fecha Prescripción Médica <Required />
                      </Label>
                      <DatePicker
                        id="prescription_date"
                        name="prescription_date"
                        selected={request.prescription_date ?? ''}
                        dateFormat="dd/MM/yyyy"
                        locale="es"
                        placeholderText="Fecha Prescripción"
                        className="date-picker form-control"
                        minDate={ moment().subtract(365, 'days').toDate() }
                        maxDate={ new Date() }
                        disabled={ isLoading }
                        onChange={date => {
                          handleInputChange({
                            target: {
                              name: 'prescription_date',
                              type: 'datepicker',
                              value: date,
                            },
                          });
                        }}
                        autoComplete="off"
                        required
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Label for="diagnosis">
                    Diagnóstico <Required />
                  </Label>
                  <Input
                    id="diagnosis"
                    name="diagnosis"
                    type="textarea"
                    invalid={formValidation.diagnosis === false}
                    value={request.diagnosis ?? ''}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    required
                  />
                </FormGroup>
                <FormGroup>
                  <Label>
                    Fechas de sesiones
                    {!request.previously_authorized
                      ? `: ${request.max_sessions} autorizadas, ${request.sessions.length} cargadas`
                      : ''}
                  </Label>
                  <Button
                    className="d-block"
                    color="primary"
                    onClick={showNewSessionModal}
                    disabled={isLoading || newSessionDisabled}
                  >
                    Agregar sesión
                  </Button>
                  {
                    !request.authorization_date
                      ? <InputError message="Se tiene que seleccionar la fecha de autorización antes de poder agregar
                      las sesiones" />
                      : null
                  }
                  <Table
                    className="align-items-center table-flush mb-0 col-sm-6"
                    style={{ fontSize: '.8rem' }}
                    responsive={false}
                  >
                    <thead>
                      <tr>
                        <th>FECHA SESIÓN</th>
                        <th>ACCIONES</th>
                      </tr>
                    </thead>
                    <tbody>
                      {request.sessions && Array.isArray(request.sessions)
                        ? request.sessions
                          .sort((a, b) => a.date.getTime() - b.date.getTime())
                          .map(({ date }, i) => (
                            <tr key={i}>
                              <td>
                                {date.getDate()}/
                                {(date.getMonth() + 1)
                                  .toString()
                                  .padStart(2, '0')}
                                  /{date.getFullYear()}
                              </td>
                              <td className="text-left table__mobile--body__actions">
                                <Button
                                  color="link"
                                  onClick={() => handleRemoveSession(i)}
                                  disabled={isLoading}
                                >
                                  <i
                                    className="fa fa-times"
                                    style={{ color: 'red' }}
                                  ></i>
                                </Button>
                              </td>
                            </tr>
                          ))
                        : null}
                    </tbody>
                  </Table>
                  {request.sessions.length === 0 ? (
                    <InputError message="Tiene que cargar al menos una sesión" />
                  ) : null}
                </FormGroup>
                <Attachments
                  files={files}
                  setFiles={setFiles}
                  disabled={isLoading}
                  setLoading={setLoading}
                  previouslyAuthorized={request.previously_authorized}
                  formData={ request }
                />
                <FormGroup>
                  <Label>Observaciones</Label>
                  <ObservationList
                    observations={request.observations}
                    disabled={isLoading}
                    createObservation={handleNewObservation}
                  />
                </FormGroup>
                <FormGroup>
                  <div className="text-center">
                    <Button
                      type="button"
                      color="primary"
                      className="btn-round mr-4"
                      onClick={handleBillingDraft}
                      disabled={isLoading}
                    >
                      Guardar Borrador
                    </Button>
                    <Button
                      type="button"
                      className="btn-round"
                      onClick={handleBillingConfirmation}
                      disabled={isLoading || disableSend}
                    >
                      Enviar Solicitud
                    </Button>
                  </div>
                </FormGroup>
              </Form>
              <AlertModal
                isOpen={showAlert}
                message={message}
                onClose={alertCallback}
              />
              <Modal isOpen={showSessionsModal} size="md" centered>
                <ModalHeader>Agregar fecha de sesión</ModalHeader>
                <ModalBody>
                  <FormGroup className="card form-group-dates">
                    <Label>Fecha</Label>
                    <DatePicker
                      id="session_date"
                      name="session_date"
                      onChange={date =>
                        setNewSessionDate(date ? date : undefined)
                      }
                      dateFormat="dd/MM/yyyy"
                      locale="es"
                      placeholderText="Fecha de la sesión"
                      minDate={newSessionDateMin ?? ''}
                      maxDate={newSessionDateMax ?? ''}
                      excludeDates={request.sessions.map(({ date }) => date)}
                      selected={newSessionDate}
                      className="form-control"
                      autoComplete="off"
                    />
                  </FormGroup>
                </ModalBody>
                <ModalFooter>
                  <Button color="success" onClick={() => handleNewSession()}>
                    Agregar
                  </Button>
                  <Button
                    color="primary"
                    onClick={() => setShowSessionsModal(false)}
                  >
                    Cancelar
                  </Button>
                </ModalFooter>
              </Modal>
              <ConfirmModal
                isOpen={confirm}
                title={confirmTitle}
                action={confirmAction}
                onClose={() => setConfirm(false)}
                onConfirm={handleConfirmation}
              />
            </CardBody>
            <CardFooter>
              <Row style={{ alignItems: 'center' }}>
                <Col sm={8}></Col>
                <Col sm={4} className="text-right">
                  <Button
                    className="btn-round"
                    color="primary"
                    onClick={() => history.push('/solicitudes')}
                  >
                    Volver
                  </Button>
                </Col>
              </Row>
            </CardFooter>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
